// prefs.ts ↔ game/preferences.cpp — the Preferences key/value store (a JSON map
// persisted through the host file API) + the Game::Load/SavePreferences slices
// that pump the option globals through it (game/game.cpp:288-321, 2565-2680).
//
// The C++ persists via HostOpenFile(HostGetPrefsFilename()) (web: IDBFS-backed
// emscripten FS); that host layer is the injected PrefStore seam below, with an
// in-memory default (localStorage/IndexedDB arrives with the browser shell).
// yajl (JsonBuilder/JsonMap) is scope-excluded — JSON.parse/JSON.stringify —
// but the JsonMap getter MISSING-KEY semantics (yajl/wrapper/jsontypes.cpp:
// 102-153: GetInteger/GetFloat -> -1, GetString -> "", GetBool -> false) are
// load-bearing (preferences.cpp:126-128's comment) and preserved exactly.

// game/ht.h:8455-8456
export const knVersionPreferencesV01 = 1;
export const knVersionPreferencesLatest = knVersionPreferencesV01;

// game/ht.h:8460-8499 — the pref key names
export const knPrefVersion = 'version';
export const kszPrefUsername = 'username';
export const kszPrefPassword = 'password';
export const kszPrefToken = 'token';
export const kfPrefAnonymous = 'anonymous';
export const knPrefYearLastRun = 'year_last_run';
export const knPrefMonthLastRun = 'month_last_run';
export const knPrefDayLastRun = 'day_last_run';
export const kwfPrefPerfOptions = 'perf_options';
export const knPrefVolume = 'volume';
export const kfPrefSoundMuted = 'sound_muted';
export const kwfPrefHandicap = 'handicap';
export const knPrefGameSpeed = 'game_speed';
export const kfPrefLassoSelection = 'lassos_election'; // (sic — the shipped key)
export const kfPrefMouseMode = 'mouse_mode';
export const knPrefHueOffset = 'hue_offset';
export const knPrefSatMultiplier = 'sat_multiplier';
export const knPrefLumOffset = 'lum_offset';
export const knPrefCxModeBest = 'cx_mode_best';
export const knPrefCyModeBest = 'cy_mode_best';
export const knPrefDepthModeBest = 'depth_mode_best';
export const knPrefDepthDataBest = 'depth_data_best';
export const knPrefSizeDataBest = 'size_data_best';
export const knPrefDegreeOrientationBest = 'degree_orientation_best';
export const knPrefCxModeLast = 'cx_mode_last';
export const knPrefCyModeLast = 'cy_mode_last';
export const knPrefDepthModeLast = 'depth_mode_last';
export const knPrefDepthDataLast = 'depth_data_last';
export const knPrefSizeDataLast = 'size_data_last';
export const knPrefDegreeOrientationLast = 'degree_orientation_last';
export const kszPrefKey = 'key';
export const knPrefDemoRank = 'demo_rank';
export const knPrefScrollSpeed = 'scroll_speed';
export const kszPrefAskUrl = 'ask_url';
export const kszPrefDeviceId = 'did';
export const knPrefUpdateDisplay = 'update_display';

// BEYOND-SPEC (F11; tasks/waves/f11-todo.md). prefs.json is SHARED with the WASM
// client (the F10 same-origin migration). The C++ reads `game_speed` as an
// integer (GetInteger) and uses it directly as a timer rate — a fractional or 0
// value would corrupt the WASM client (a 0 rate spins it). So the SP-only 11x
// notch is persisted as: game_speed = 1 (the WASM client sees its own max, 8.0x)
// PLUS this TS-only boolean the TS client reads FIRST. A WASM-written prefs doc
// has no such key → GetBool returns false (prefs.cpp:165-167) → the TS client
// reads the integer game_speed, exactly the pre-F11 behavior.
export const kfPrefTsGameSpeed11 = 'ts_game_speed_11';
// The SP 11x sentinel (gameoptions.ts kt11x = 8/11) — the only sub-1 game speed.
// io/ stays ui-import-free, so the value is mirrored here (a 2-digit rational).
export const ktGameSpeed11 = 8 / 11;
// The WASM-safe integer written to game_speed when the 11x notch is active (the
// C++ table's max; the WASM client clamps to 8.0x). Math.trunc(kctUpdate/2) = 4
// is the default-prefs value; the 11x write uses 1 = the C++ fastest integer.
export const knGameSpeed11WasmFallback = 1;

// ht.h:283-284
export const kfPerfAll = 0x03ff;
export const kfPerfMax = 0xffff;
// ht.h:1018
export const kctUpdate = 8;
// ht.h:1017
export const kcmsUpdate = 80;

/** The HostOpenFile(HostGetPrefsFilename())/HostRead/WriteFile seam
 *  (game/preferences.cpp:46-78/140-149): one JSON document by fixed name. */
export interface PrefStore {
  /** kfOfRead: the whole prefs file, or null when it doesn't exist/is empty. */
  read(): string | null;
  /** kfOfWrite: replace the prefs file; false = the HostWriteFile failure arm. */
  write(json: string): boolean;
}

/** The in-memory PrefStore default (IndexedDB lands with the browser shell). */
export class MemoryPrefStore implements PrefStore {
  private json: string | null = null;
  read(): string | null {
    return this.json;
  }
  write(json: string): boolean {
    this.json = json;
    return true;
  }
}

/** HostGetCurrentDate (game/preferences.cpp:108-112) — nMonth/nDay 1-based as
 *  the SDL host fills them (game/sdl/host.cpp localtime +1 arms). */
export interface PrefsDate {
  nYear: number;
  nMonth: number;
  nDay: number;
}

export class Preferences {
  // game/preferences.cpp:6-9 (ctor)
  private m_pmap: Record<string, unknown> | null = null;
  private m_pszJson: string | null = null;

  // game/preferences.cpp:45-103 — read the whole store, parse, require a map
  InitFromFile(store: PrefStore): boolean {
    const psz = store.read(); // HostOpenFile/HostSeek/HostReadFile
    if (psz === null || psz.length === 0) return false;
    this.m_pszJson = psz;

    let obj: unknown;
    try {
      obj = JSON.parse(psz); // json::JsonBuilder Update/End
    } catch {
      return false;
    }

    // The root of a prefs file must be a JSON object. Anything else (array,
    // scalar, or a corrupt/truncated file) would be misused as a JsonMap below.
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return false;

    this.m_pmap = obj as Record<string, unknown>;
    return true;
  }

  // game/preferences.cpp:105-137
  InitFromDefaults(date: PrefsDate, pszDeviceId: string): boolean {
    this.m_pmap = {};

    this.Set(knPrefYearLastRun, date.nYear);
    this.Set(knPrefMonthLastRun, date.nMonth);
    this.Set(knPrefDayLastRun, date.nDay);

    this.SetBool(kfPrefAnonymous, false);
    // Default to full volume (255), matching the shipped WASM web build — its SDL/Emscripten
    // SetVolume was effectively a no-op so it always played at full; 100/255 (~39%) made every
    // sound noticeably quieter on a fresh TS install.
    this.Set(knPrefVolume, 255);
    this.SetBool(kfPrefSoundMuted, false);
    this.Set(kwfPrefPerfOptions, kfPerfMax);
    this.Set(knPrefGameSpeed, Math.trunc(kctUpdate / 2));
    this.Set(kwfPrefHandicap, kfHcapDefault);
    this.SetFloat(knPrefScrollSpeed, 1.0);
    this.SetString(kszPrefAskUrl, 'http://');
    this.SetString(kszPrefDeviceId, pszDeviceId); // HostGenerateDeviceId
    this.Set(knPrefUpdateDisplay, 8); // 125 FPS

    // Input + color-adjust defaults. LoadPreferences reads these unconditionally;
    // without a default GetInteger/GetBool return -1/false for a fresh install
    // (e.g. the HSL sliders would initialize to -1 instead of the neutral 0).

    this.SetBool(kfPrefLassoSelection, false);
    this.SetBool(kfPrefMouseMode, false);
    this.Set(knPrefHueOffset, 0);
    this.Set(knPrefSatMultiplier, 0);
    this.Set(knPrefLumOffset, 0);
    this.Set(knPrefDemoRank, 0);

    return true;
  }

  // game/preferences.cpp:139-151
  Save(store: PrefStore): boolean {
    const json = JSON.stringify(this.m_pmap); // m_pmap->ToJson()
    return store.write(json); // HostOpenFile/HostWriteFile
  }

  // game/preferences.cpp:153-155 (JsonMap::GetString — "" when missing/not-string)
  GetString(key: string): string {
    const v = this.m_pmap![key];
    return typeof v === 'string' ? v : '';
  }

  // game/preferences.cpp:157-159 (JsonMap::GetInteger — -1 when missing/not-number)
  GetInteger(key: string): number {
    const v = this.m_pmap![key];
    return typeof v === 'number' ? Math.trunc(v) : -1;
  }

  // game/preferences.cpp:161-163 (JsonMap::GetFloat — -1 when missing/not-number)
  GetFloat(key: string): number {
    const v = this.m_pmap![key];
    return typeof v === 'number' ? v : -1;
  }

  // game/preferences.cpp:165-167 (JsonMap::GetBool — false when missing/not-bool)
  GetBool(key: string): boolean {
    const v = this.m_pmap![key];
    return typeof v === 'boolean' ? v : false;
  }

  // game/preferences.cpp:169-171 — Set(key, const char *)
  SetString(key: string, psz: string): void {
    this.m_pmap![key] = psz;
  }

  // game/preferences.cpp:173-175 — Set(key, int)
  Set(key: string, n: number): void {
    this.m_pmap![key] = n;
  }

  // game/preferences.cpp:177-179 — Set(key, float)
  SetFloat(key: string, n: number): void {
    this.m_pmap![key] = n;
  }

  // game/preferences.cpp:181-183 — Set(key, bool)
  SetBool(key: string, f: boolean): void {
    this.m_pmap![key] = f;
  }
}

// game/preferences.cpp:17-29
export function PrefsFromFile(store: PrefStore): Preferences | null {
  const pprefs = new Preferences();
  if (!pprefs.InitFromFile(store)) return null;
  return pprefs;
}

// game/preferences.cpp HostGenerateDeviceId — a random, non-PII per-browser id (no cookie; resets
// on cleared site data). Shared by the from-defaults init and the load-time back-fill below.
const genDeviceId = (): string => Math.random().toString(16).slice(2);

// game/preferences.cpp:31-43 (HostGetCurrentDate/HostGenerateDeviceId defaults
// come from the web host: the wall clock + a random id)
export function PrefsFromDefaults(date?: PrefsDate, pszDeviceId?: string): Preferences | null {
  const pprefs = new Preferences();
  const d = new Date();
  if (
    !pprefs.InitFromDefaults(
      date ?? { nYear: d.getFullYear(), nMonth: d.getMonth() + 1, nDay: d.getDate() },
      pszDeviceId ?? genDeviceId(),
    )
  )
    return null;
  return pprefs;
}

// game/game.cpp:2565-2579 — Game::LoadPreferences (file -> defaults fallback)
export function LoadPreferences(store: PrefStore): Preferences | null {
  // Try to load preferences.
  let gpprefs = PrefsFromFile(store);

  // Failed? Initialize preferences to default values
  if (gpprefs === null) {
    gpprefs = PrefsFromDefaults();
  }
  // Web device-id back-fill: a `did` is minted only by InitFromDefaults (fresh prefs). Prefs SAVED
  // before the device-id feature existed have no `did`, and SavePreferences only round-trips it — so
  // a returning player would stay id-less forever and their SP telemetry couldn't attribute to a
  // device. Mint + persist one once if missing so the id is stable across sessions (no-op once set).
  if (gpprefs !== null && !gpprefs.GetString(kszPrefDeviceId)) {
    gpprefs.SetString(kszPrefDeviceId, genDeviceId());
    gpprefs.Save(store);
  }
  return gpprefs;
}

// game/ht.h:288-298 (mirrored in sim/src/player/side.ts — duplicated here so io/
// stays sim-import-free)
export const kfHcapEasy = 0x0002 | 0x0040 | 0x0001 | 0x0004 | 0x0010 | 0x0020;
export const kfHcapNormal = 0x0002 | 0x0040 | 0x0010 | 0x0020;
export const kfHcapHard = 0x0010;
export const kfHcapDefault = kfHcapNormal;

/** The option-global slice both apply/save touch (ui/shellform.ts OptionsState
 *  is structurally compatible — io/ stays ui-import-free). */
export interface PrefOptionsSlice {
  fLassoSelection: boolean;
  fMouseMode: boolean;
  nScrollSpeed: number;
  wfHandicap: number;
  nHueOffset: number;
  nSatMultiplier: number;
  nLumOffset: number;
  wfPerfOptions: number;
  nDemoRank: number;
}

// game/game.cpp:292-321 — Game::Init's "Suck what we can from preferences"
// block. Returns the new gtGameSpeed. (The username/password/token/anonymous/
// askUrl/deviceId globals are M5 login state; gcmsDisplayUpdate is the display
// host's repaint cap — both stay in the prefs map untouched.)
//
// forceMouseMode: the C++ web build hard-codes gfMouseMode=true (game.cpp:296-301,
// "web == desktop mouse"). On a touch device (phone/tablet) that wrongly forces
// the StylusHandler instead of the touch FingerHandler, so the web boundary passes
// false there and the saved mouse_mode pref (default false) decides. Default true
// preserves the desktop-web behavior for every other caller.
export function ApplyPreferences(
  gpprefs: Preferences,
  options: PrefOptionsSlice,
  gtGameSpeed: number,
  forceMouseMode = true,
): number {
  // F11 SP-only 11x notch: read the TS-only key FIRST. When set, the live TS
  // game speed is the fractional sentinel (the WASM-safe integer game_speed,
  // written alongside, is what the WASM client reads). A WASM-format doc has no
  // such key (GetBool→false) → fall through to the integer, the pre-F11 path.
  if (gpprefs.GetBool(kfPrefTsGameSpeed11)) {
    gtGameSpeed = ktGameSpeed11;
  } else {
    gtGameSpeed = gpprefs.GetInteger(knPrefGameSpeed);
  }
  options.fLassoSelection = gpprefs.GetBool(kfPrefLassoSelection);
  options.fMouseMode = gpprefs.GetBool(kfPrefMouseMode);
  // #if defined(WEB) — the web build is mouse-driven: enable mouse mode so a
  // single pointer uses the stylus UI and can drag a selection rectangle. Skipped
  // on touch-primary devices (forceMouseMode=false) so they get the FingerHandler.
  if (forceMouseMode) options.fMouseMode = true;
  options.nHueOffset = gpprefs.GetInteger(knPrefHueOffset);
  options.nSatMultiplier = gpprefs.GetInteger(knPrefSatMultiplier);
  options.nLumOffset = gpprefs.GetInteger(knPrefLumOffset);
  options.wfPerfOptions = gpprefs.GetInteger(kwfPrefPerfOptions) & 0xffff;
  options.wfHandicap = gpprefs.GetInteger(kwfPrefHandicap) & 0xffff;
  options.nDemoRank = gpprefs.GetInteger(knPrefDemoRank);
  options.nScrollSpeed = gpprefs.GetFloat(knPrefScrollSpeed);
  return gtGameSpeed;
}

// game/game.cpp:2582-2680 — Game::SavePreferences, the web-reachable slice:
// the kfGameInitDone gate is the caller's, the login fields (username/password/
// token/anonymous) and ask-url/device-id round-trip their loaded values until
// M5 login lands, the display-mode blocks take the gpdisp==NULL zero arms (the
// web display has one fixed mode), the DRM key/WIN scale arms are platform.
export function SavePreferences(
  gpprefs: Preferences,
  store: PrefStore,
  gsndm: { GetVolume(): number; IsEnabled(): boolean },
  options: PrefOptionsSlice,
  gtGameSpeed: number,
  date?: PrefsDate,
): boolean {
  // Always save the latest version of the preferences

  gpprefs.Set(knPrefVersion, knVersionPreferencesLatest);
  gpprefs.SetString(kszPrefUsername, gpprefs.GetString(kszPrefUsername)); // M5: gszUsername
  gpprefs.SetString(kszPrefPassword, gpprefs.GetString(kszPrefPassword)); // M5: gszPassword
  gpprefs.SetString(kszPrefToken, gpprefs.GetString(kszPrefToken)); // M5: gszToken
  gpprefs.SetBool(kfPrefAnonymous, gpprefs.GetBool(kfPrefAnonymous)); // M5: gfAnonymous

  const d = new Date();
  const dT = date ?? { nYear: d.getFullYear(), nMonth: d.getMonth() + 1, nDay: d.getDate() };
  gpprefs.Set(knPrefYearLastRun, dT.nYear);
  gpprefs.Set(knPrefMonthLastRun, dT.nMonth);
  gpprefs.Set(knPrefDayLastRun, dT.nDay);

  gpprefs.Set(knPrefVolume, gsndm.GetVolume());
  gpprefs.SetBool(kfPrefSoundMuted, !gsndm.IsEnabled());
  gpprefs.Set(
    kwfPrefPerfOptions,
    ((options.wfPerfOptions & kfPerfAll) | (kfPerfMax & ~kfPerfAll)) & 0xffff,
  );
  // F11: NEVER write a fractional/0 game_speed (the C++ reads it as a timer-rate
  // integer; 0 spins it). When the SP 11x notch is active, write the WASM-safe
  // integer and the TS-only flag; otherwise write the integer speed and CLEAR
  // the flag (so dropping back to a normal speed un-sets the 11x marker).
  if (gtGameSpeed > 0 && gtGameSpeed < 1) {
    gpprefs.Set(knPrefGameSpeed, knGameSpeed11WasmFallback);
    gpprefs.SetBool(kfPrefTsGameSpeed11, true);
  } else {
    gpprefs.Set(knPrefGameSpeed, Math.trunc(gtGameSpeed));
    gpprefs.SetBool(kfPrefTsGameSpeed11, false);
  }
  gpprefs.SetBool(kfPrefLassoSelection, options.fLassoSelection);
  gpprefs.SetBool(kfPrefMouseMode, options.fMouseMode);
  gpprefs.Set(knPrefHueOffset, options.nHueOffset);
  gpprefs.Set(knPrefSatMultiplier, options.nSatMultiplier);
  gpprefs.Set(knPrefLumOffset, options.nLumOffset);
  gpprefs.Set(kwfPrefHandicap, options.wfHandicap);
  gpprefs.Set(knPrefDemoRank, options.nDemoRank);
  gpprefs.SetFloat(knPrefScrollSpeed, options.nScrollSpeed);
  gpprefs.SetString(kszPrefAskUrl, gpprefs.GetString(kszPrefAskUrl));
  gpprefs.SetString(kszPrefDeviceId, gpprefs.GetString(kszPrefDeviceId));

  // gpdisp == NULL / m_immBest == -1 arms (the web display has one fixed mode)
  gpprefs.Set(knPrefCxModeBest, 0);
  gpprefs.Set(knPrefCyModeBest, 0);
  gpprefs.Set(knPrefDepthModeBest, 0);
  gpprefs.Set(knPrefDepthDataBest, 0);
  gpprefs.Set(knPrefSizeDataBest, 0);
  gpprefs.Set(knPrefDegreeOrientationBest, 0);
  gpprefs.Set(knPrefCxModeLast, 0);
  gpprefs.Set(knPrefCyModeLast, 0);
  gpprefs.Set(knPrefDepthModeLast, 0);
  gpprefs.Set(knPrefDepthDataLast, 0);
  gpprefs.Set(knPrefSizeDataLast, 0);
  gpprefs.Set(knPrefDegreeOrientationLast, 0);

  // (kszPrefKey is the DRM key — drm.cpp is scope-excluded; knPrefScale is WIN)
  const nUpdateDisplay = gpprefs.GetInteger(knPrefUpdateDisplay);
  gpprefs.Set(knPrefUpdateDisplay, nUpdateDisplay >= 0 ? nUpdateDisplay : 8);

  const f = gpprefs.Save(store);

  // HostFlushFileSystemChanges() — the IDBFS push is the PrefStore's concern
  return f;
}
